import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, from, Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {User} from '../../_models/user.model';
import {environment} from '../../../environments/environment';
import {UserService} from './user.service';
import {FederatedSignInOptionsCustom} from '@aws-amplify/auth/lib-esm/types';
import {AmplifyService} from 'aws-amplify-angular';
import {CognitoUser} from 'amazon-cognito-identity-js';
import * as uuid from 'uuid';
import { SessionManagementService } from './session-management.service';
import { Constants } from '../../shared/constants';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly apiName: string;
  private currentUserSub = new BehaviorSubject<User>(null);
  readonly currentUser = this.currentUserSub.asObservable();

  constructor(private http: HttpClient,
              private amplifyService: AmplifyService,
              private userService: UserService) {
    this.pushCurrentUser(JSON.parse(
      sessionStorage.getItem(Constants.AUTH_TOKEN)));
    this.apiName = environment.api.endpoints[0].name;
  }

  public get currentUserValue(): User {
    return this.currentUserSub.value;
  }

  private pushCurrentUser(currentUser: User) {
    this.currentUserSub.next(currentUser);
  }

  createIdentity(): Observable<any> {
    const identityObservable = new Observable(observer => {
      this.amplifyService.auth().currentSession().then(sessionData => {
        console.log('CurrentSession Data: ', sessionData);
        this.amplifyService.auth().currentUserPoolUser().then((userData: any) => {
          console.log('CurrentUserPool Data: ', userData);
          (<CognitoUser>userData).getUserAttributes((err, attributes) => {
            console.log('CurrentUser Attributes: ', attributes);
            const user: User = this.userService.createUser(attributes);
            observer.next(user);
            observer.complete();
          });
        }).catch((userPoolError: any) => {
          console.log('CurrentUserPool Error: ', userPoolError);
          observer.next(false);
          observer.complete();
        });
      }).catch((sessionErr) => {
        console.log('CurrentSession Error: ', sessionErr);
        observer.error(sessionErr);
      });
    });
    return identityObservable;
  }

  isAuthenticated(): Observable<any> {
    console.log('isAuthenticated....');
    const userAuthObservable = new Observable(observer => {
      this.amplifyService.auth().currentSession().then((sessionData: any) => {
        console.log('CurrentSession Data: ', sessionData);
        this.amplifyService.auth().currentUserPoolUser().then((userData: any) => {
          console.log('CurrentUser Data: ', userData);
          observer.next(true);
          observer.complete();
        }).catch((userPoolError: any) => {
          console.log('CurrentUserPool Error: ', userPoolError);
          observer.next(false);
          observer.complete();
        });
      }).catch((sessionError: any) => {
        console.log('CurrentSession Error: ', sessionError);
        observer.next(false);
        observer.complete();
      });
    });

    return userAuthObservable;
  }

  public saveLoginSession(user: User): Observable<any> {
    const url = `/login-session`;
    const content = {
      headers: this.getHeaders(user),
      body: user
    };
    return from(this.amplifyService.api().post(this.apiName, url, content)).pipe(
      map((usr: User) => {
        // store user details and jwt token in session storage
        // to keep user logged in between page refreshes
        sessionStorage.setItem(Constants.AUTH_TOKEN, JSON.stringify(usr));
        this.currentUserSub.next(usr);
        return usr;
      }),
      catchError(this.handleError<User>('Error in saveLoginSession()!!', new User())));
  }

  public updateLoginSession(user: User): Observable<any> {
    const url = `/logout-session`;
    this.log(`updateLoginSession for loginHistoryId ${user}`);
    const content = {
      headers: this.getHeaders(user),
      body: user
    };
    return from(this.amplifyService.api().post(this.apiName, url, content)).pipe(
      tap(_ => this.log(`API call updateLoginSession for loginHistoryId=${user.loginHistoryId}`)),
      catchError(this.handleError<User>('Error in updateLoginSession()!!', new User())));
  }

  login() {
    console.log('Call Federated Sign In (Internal): ', environment.internal);
    const options: FederatedSignInOptionsCustom = {
      customProvider: environment.internal ?
        environment.int.customProvider : environment.ext.customProvider
    };

    this.amplifyService.auth().federatedSignIn(options);
  }

  public logout(logoutReasonTypecode:string) {
    // Update Login History and Logout User
    if(this.currentUserValue){
      this.currentUserValue.logoutReasonTypeCode=logoutReasonTypecode;
      this.updateLoginSession(this.currentUserValue).subscribe(
        data => {
          sessionStorage.removeItem(Constants.AUTH_TOKEN);
          sessionStorage.removeItem(Constants.SESSION_CONTEXT);
          this.amplifyService.auth().signOut();
        });
    }
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a CaseService message with the MessageService */
  private log(message: string) {
    console.log(message);
  }

  private getHeaders(user: User) {
    const local_user = JSON.parse(sessionStorage.getItem(Constants.AUTH_TOKEN));
    if (local_user) {
      return {
        'Content-Type': 'application/json',
        'userid': String(local_user.userId),
        'guid': String(local_user.guId),
        'sessionid': String(local_user.loginHistoryId),
        'requestid': uuid.v4()
      };
    }
    return {
      'Content-Type': 'application/json',
      'userid': '-1',
      'guid': String(user.guid),
      'sessionid': 'first_call',
      'requestid': uuid.v4()
    };
  }

}
